import { Component } from "react";
import { withTranslation } from "react-i18next";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Paper,
	Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
// services
import User from "../../services/user";
import Gupport from "../../services/gupport";
// types
import type { WithTranslation } from "react-i18next";
import type { GridColDef } from "@mui/x-data-grid";
import type { UserId } from "../../types/user";
import type { CmdDeleteB2CToken, CmdGetB2CTokens, UserB2CToken } from "../../types/gupport";

type Props = Readonly<WithTranslation & {
	userId: UserId;
}>;

type State = {
	loading: boolean;
	inProgress: boolean;
	error: Error | null;
	selectedToken: UserB2CToken | null;
	tokens: ReadonlyArray<UserB2CToken>;
	openDeleteTokenDialog: string | undefined;
};

const DIALOG_TYPES = {
	ALL: "all",
	ONE: "one",
} as const;

class B2CTokens extends Component<Props, State> {

	constructor(props: Props) {
		super(props);

		this.state = {
			loading: false,
			inProgress: false,
			error: null,
			selectedToken: null,
			tokens: [],
			openDeleteTokenDialog: undefined,
		};

		this.refresh = this.refresh.bind(this);
		this.fetchTokens = this.fetchTokens.bind(this);
		this.handleClose = this.handleClose.bind(this);
	}

	override componentDidMount() {
		this.fetchTokens(this.props.userId);
	}

	refresh() {
		this.fetchTokens(this.props.userId);
	}

	fetchTokens(userId) {
		this.setState({
			loading: true,
		});

		const cmd = {
			action: "getB2CTokens",
			username: userId,
		} as const satisfies CmdGetB2CTokens;
		Gupport.send(cmd, (_error, msg) => {
			if (!_error && msg?.payload.status === "ok") {
				this.setState({
					loading: false,
					error: null,
					tokens: msg.payload.data.tokens,
				});
			} else {
				this.setState({
					loading: false,
					error: _error,
					tokens: [],
				});
			}
		});
	}

	showDeleteDialog(type, token) {
		this.setState({
			openDeleteTokenDialog: type,
			selectedToken: token,
		});
	}

	handleClose() {
		this.setState({
			openDeleteTokenDialog: undefined,
			selectedToken: null,
		});
	}

	deleteToken(deleteAll: boolean = false) {
		this.setState({
			inProgress: true,
		});

		const cmd = {
			action: "deleteB2CToken",
			data: {
				tokens: deleteAll ? this.state.tokens : [this.state.selectedToken!], // eslint-disable-line @typescript-eslint/no-non-null-assertion
			},
			username: this.props.userId,
		} as const satisfies CmdDeleteB2CToken;
		Gupport.send(cmd, (_error, msg) => {
			if (!_error && msg?.payload.status === "ok") {
				this.fetchTokens(this.props.userId);
			}
			this.setState({
				inProgress: false,
				selectedToken: null,
				openDeleteTokenDialog: undefined,
			});
		});
	}

	override render() {
		const { t } = this.props;
		if (this.state.loading) {
			return <CircularProgress />;
		}
		if (this.state.error) {
			return <div>{this.state.error.message}</div>;
		}

		const columns = [
			{
				field: "id",
				headerName: t("users.b2cTokens.token"),
				flex: 5,
				renderCell: (params) => (<Tooltip placement="bottom" title={params.value}>{params.value}</Tooltip>),
			},
			{
				field: "logon_id",
				headerName: t("users.b2cTokens.logonId"),
				flex: 5,
				renderCell: (params) => (<Tooltip placement="bottom" title={params.value}>{params.value}</Tooltip>),
			},
			{
				field: "logon_ts",
				headerName: t("users.b2cTokens.endtime"),
				flex: 4,
				renderCell: (params) => (
					params.value ? <Tooltip placement="bottom" title={new Date(params.value * 1000).toLocaleString()}><time dateTime={new Date(params.value * 1000).toISOString()}>{new Date(params.value * 1000).toLocaleString()}</time></Tooltip> : null
				),
			},
			{
				field: "refresh_token_exp",
				headerName: t("users.b2cTokens.expiry"),
				flex: 4,
				renderCell: (params) => (
					<Tooltip placement="bottom" title={new Date(params.value * 1000).toLocaleString()}><time dateTime={new Date(params.value * 1000).toISOString()}>{new Date(params.value * 1000).toLocaleString()}</time></Tooltip>
				),
			},
			{
				field: "created",
				headerName: t("users.b2cTokens.lastRefresh"),
				flex: 4,
				renderCell: (params) => (
					<Tooltip placement="bottom" title={new Date(params.value).toLocaleString()}><time dateTime={new Date(params.value).toISOString()}>{new Date(params.value).toLocaleString()}</time></Tooltip>
				),
			},
			{
				field: "policy",
				headerName: t("users.b2cTokens.policy"),
				flex: 4,
				renderCell: (params) => (<Tooltip placement="bottom" title={params.value}>{params.value}</Tooltip>),
			},
			{
				field: "agent",
				headerName: t("users.b2cTokens.agent"),
				flex: 2,
				renderCell: (params) => (<Tooltip placement="bottom" title={params.value}>{params.value}</Tooltip>),
			},
			{
				field: "action",
				headerName: t("users.b2cTokens.actions"),
				flex: 3,
				renderCell: (params) => (
					<Button variant="contained" color="danger" onClick={this.showDeleteDialog.bind(this, DIALOG_TYPES.ONE, params.row)}>
						{t("users.delete")}
					</Button>
				),
			},
		] as const satisfies ReadonlyArray<GridColDef<UserB2CToken>>;

		const hasWriteAccess = User.hasLevel("user_b2c_write");

		return (
			<>
				{hasWriteAccess &&
					<div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", margin: "8px 8px 8px 0" }}>
						<Button variant="contained" color="danger" onClick={this.showDeleteDialog.bind(this, DIALOG_TYPES.ALL)}>{t("users.b2cTokens.deleteAll")}</Button>
					</div>
				}
				<Paper sx={{ overflow: "hidden" }}>
					<DataGrid
						columns={columns}
						rows={this.state.tokens}
						checkboxSelection={false}
						columnVisibilityModel={{
							action: hasWriteAccess,
						}}
					/>
				</Paper>
				{hasWriteAccess &&
					<Dialog
						fullWidth={true}
						open={Boolean(this.state.openDeleteTokenDialog)}
						onClose={this.handleClose}
					>
						<DialogTitle>{t(this.state.openDeleteTokenDialog === DIALOG_TYPES.ONE ? "users.b2cTokens.dialogDelete" : "users.b2cTokens.dialogDeleteAll")}</DialogTitle>
						<DialogContent>
							<DialogContentText>
								{t(this.state.openDeleteTokenDialog === DIALOG_TYPES.ONE ? "users.b2cTokens.deleteConfirm" : "users.b2cTokens.deleteAllConfirm")}
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button
								color="danger"
								loading={this.state.inProgress}
								onClick={this.deleteToken.bind(this, this.state.openDeleteTokenDialog === DIALOG_TYPES.ALL)}
							>
								{t(this.state.openDeleteTokenDialog === DIALOG_TYPES.ONE ? "dialog.delete" : "users.b2cTokens.deleteAll")}
							</Button>
							<Button color="inherit" onClick={this.handleClose}>{t("dialog.cancel")}</Button>
						</DialogActions>
					</Dialog>
				}
			</>
		);
	}

}

export default withTranslation()(B2CTokens);
